/**
 * ContactsPage — HeyMotek contacts list (slim shell).
 *
 * Owns the filter/sort/expansion state and composes the extracted pieces:
 *   - useContactsQuery (lib)       — the infinite query + derived lists
 *   - ContactsToolbar              — segment / search / date filters
 *   - ContactsTable                — desktop table (columns from CONTACTS_COLUMNS)
 *   - MobileContactList + Drawer   — mobile compressed view + expanded detail
 *
 * Filters: date preset / custom range → server-side; sort + cursor →
 * server-side; segment chip + search → client-side over the loaded set.
 * "Your number" + "Client" columns show only for multi-client (admin) view.
 *
 * History + decisions: REFERENCE/plans/PLAN_CONTACTSPAGE_REFACTOR.md (split + dedup +
 * column single-source, 2026-06-06). Originally a single ~1700-line file.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DateRange } from 'react-day-picker';
import { Download, Lock } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { usePageMaxWidth } from '@/lib/pageLayout';
import { useIsMobile } from '@/lib/useIsMobile';
import { useContactsQuery } from '@/lib/useContactsQuery';
import {
  exportToCsv,
  type DatePreset,
  type Segment,
  type SortField,
} from '@/lib/contacts';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState } from '@/components/ErrorState';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { ContactsToolbar } from '@/components/contacts/ContactsToolbar';
import { ContactsTable } from '@/components/contacts/ContactsTable';
import {
  MobileContactList,
  MobileHiddenColumns,
} from '@/components/contacts/MobileContactList';
import { ExpandedDetail } from '@/components/contacts/ExpandedDetail';
import { ContactsPageSkeleton } from '@/components/contacts/ContactsPageSkeleton';

export function ContactsPage() {
  const { me, clients, loading: authLoading } = useAuth();

  // Locked-rows banner data (free tier only). Same query key as the Dashboard
  // + Account usage widgets, so it's cache-shared, not an extra request.
  const usageQuery = useQuery({
    queryKey: ['usage'],
    queryFn: () =>
      apiRequest<{ locked_count: number; effective_tier: string }>('/usage'),
    staleTime: 30_000,
    enabled: me?.effective_tier === 'free',
  });
  const lockedCount =
    me?.effective_tier === 'free' ? (usageQuery.data?.locked_count ?? 0) : 0;
  const showClient = clients.length > 1;
  const showYourNumber = clients.length > 1;
  const colVis = { showClient, showYourNumber };
  const isMobile = useIsMobile();

  // Page-canvas floor stays at the AppShell default (max-w-7xl) for the typical
  // 5-column case. When Line + Client demo columns become visible
  // (admin / Multi-WA Pro / Agency), bump to screen-2xl so the table doesn't
  // push Status off the right edge.
  usePageMaxWidth(showClient || showYourNumber ? 'max-w-screen-2xl' : null);

  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [segment, setSegment] = useState<Segment>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { query, contacts, filtered, counts } = useContactsQuery({
    datePreset,
    customRange,
    segment,
    search,
    sortField,
    sortOrder,
    authLoading,
  });

  if (authLoading || query.isLoading) {
    return <ContactsPageSkeleton colVis={colVis} />;
  }

  if (query.isError) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <PageHeader title="Contacts" subtitle="Couldn't load this page." />
        <ErrorState
          title="Couldn't load your contacts"
          onRetry={() => query.refetch()}
        />
      </div>
    );
  }

  function pickSort(field: SortField) {
    if (field === sortField) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setExpandedId(null);
  }

  function applyCustomRange(r: DateRange | undefined) {
    setCustomRange(r);
    setDatePreset('custom');
    setExpandedId(null);
  }

  function toggleExpand(id: string) {
    setExpandedId((cur) => (cur === id ? null : id));
  }

  const expandedContact = expandedId
    ? (contacts.find((c) => c.id === expandedId) ?? null)
    : null;

  const pagination = {
    visibleCount: filtered.length,
    canLoadMore: !!query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    onLoadMore: () => query.fetchNextPage(),
  };

  const emptyHint =
    search || segment !== 'all' || datePreset !== '30d'
      ? 'No contacts match this filter.'
      : 'No contacts yet. Your first contact will show up here.';

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <PageHeader
        title="Contacts"
        subtitle={
          <>
            <span className="font-bold text-dark">{counts.all}</span> matched to
            the ads that brought them.
          </>
        }
        action={
          <button
            type="button"
            onClick={() => exportToCsv(filtered, showClient)}
            disabled={filtered.length === 0}
            className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-lg border-2 border-green-700 bg-white px-4 py-2 text-sm font-semibold text-dark transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 sm:self-auto"
          >
            <Download className="h-4 w-4 text-green-700" />
            Export CSV
          </button>
        }
      />

      <ContactsToolbar
        segment={segment}
        onSegment={(s) => {
          setSegment(s);
          setExpandedId(null);
        }}
        search={search}
        onSearch={setSearch}
        datePreset={datePreset}
        onPreset={(p) => {
          setDatePreset(p);
          setCustomRange(undefined);
          setExpandedId(null);
        }}
        customRange={customRange}
        onCustomRange={applyCustomRange}
        counts={counts}
      />

      {lockedCount > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-dark/5 bg-bg-tint px-4 py-3">
          <Lock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-dark">
            <span className="font-bold">
              {lockedCount} more contact{lockedCount === 1 ? '' : 's'} this
              month
            </span>{' '}
            {lockedCount === 1 ? 'is' : 'are'} locked on the Free plan.{' '}
            <Link
              to="/account"
              className="font-semibold text-info hover:underline"
            >
              Upgrade to see them all
            </Link>
            .
          </p>
        </div>
      )}

      <ContactsTable
        rows={filtered}
        colVis={colVis}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={pickSort}
        expandedId={expandedId}
        onToggleExpand={toggleExpand}
        emptyHint={emptyHint}
        pagination={pagination}
      />

      <MobileContactList
        rows={filtered}
        onOpen={toggleExpand}
        pagination={pagination}
      />
      <p className="mt-3 text-center text-xs text-muted-foreground md:hidden">
        Tap a contact for full detail
      </p>

      {/* Mobile-only Drawer for expanded detail. Desktop uses the inline-expand
          <tr> path inside ContactsTable. */}
      <Drawer
        open={isMobile && !!expandedId}
        onOpenChange={(open) => !open && setExpandedId(null)}
      >
        <DrawerContent>
          <DrawerTitle className="sr-only">
            Contact{' '}
            {expandedContact?.profile_name ?? expandedContact?.phone ?? ''}
          </DrawerTitle>
          <div className="max-h-[80vh] overflow-y-auto px-4 pb-6 pt-2">
            {expandedContact && (
              <>
                <MobileHiddenColumns contact={expandedContact} colVis={colVis} />
                <ExpandedDetail contact={expandedContact} />
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
